import React from 'react';
import { Crown, Star, User, Phone, Mail } from 'lucide-react';
import { isVipActive, formatVipExpiry } from './constants';

const CustomerTable = ({ customers, pagination, page, setPage, isAdmin, onOpenDetail, onOpenVip }) => (
  <div className="bg-white rounded-xl shadow overflow-hidden">
    <table className="w-full">
      <thead className="bg-gray-50 border-b">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">客户</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">联系方式</th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">VIP</th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">订单数</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">累计消费</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">余额</th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">返佣</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">最近下单</th>
          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {customers.map(customer => (
          <tr key={customer.id} className="hover:bg-gray-50">
            <td className="px-4 py-3">
              <div className="flex items-center space-x-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  customer.is_agent ? 'bg-purple-100' : isVipActive(customer) ? 'bg-yellow-100' : 'bg-blue-100'
                }`}>
                  {customer.is_agent
                    ? <Star className="w-5 h-5 text-purple-600" />
                    : isVipActive(customer)
                      ? <Crown className="w-5 h-5 text-yellow-600" />
                      : <User className="w-5 h-5 text-blue-600" />}
                </div>
                <div>
                  {/* 名称 + 标签 */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <p className="font-medium text-sm">{customer.name || customer.username || '匿名'}</p>
                    {customer.is_agent === 1 && (
                      <span className="px-1 py-0.5 text-xs rounded bg-purple-100 text-purple-700">代理</span>
                    )}
                    {customer.tg_id && (
                      <span className="px-1 py-0.5 text-xs rounded bg-blue-100 text-blue-500">TG</span>
                    )}
                  </div>
                  {/* 系统 ID */}
                  <p className="text-xs text-gray-300">#{customer.id}</p>
                  {/* 用户名 */}
                  {customer.username && (
                    <p className="text-xs text-gray-400">@{customer.username}</p>
                  )}
                  {/* TG 用户名 */}
                  {customer.tg_username && (
                    <p className="text-xs text-blue-400">✈️ @{customer.tg_username}</p>
                  )}
                </div>
              </div>
            </td>
            <td className="px-4 py-3">
              <div className="space-y-0.5">
                {customer.phone && (
                  <p className="text-sm flex items-center space-x-1">
                    <Phone className="w-3 h-3 text-gray-400" />
                    <span>{customer.phone}</span>
                  </p>
                )}
                {customer.email && (
                  <p className="text-sm flex items-center space-x-1 text-gray-500">
                    <Mail className="w-3 h-3 text-gray-400" />
                    <span>{customer.email}</span>
                  </p>
                )}
                {/* TG ID */}
                {customer.tg_id && (
                  <p className="text-xs text-gray-400">TG: {customer.tg_id}</p>
                )}
                {!customer.phone && !customer.email && !customer.tg_id && (
                  <p className="text-xs text-gray-400">未填写</p>
                )}
              </div>
            </td>
            <td className="px-4 py-3 text-center">
              {isVipActive(customer) ? (
                <div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                    <Crown className="w-3 h-3" />VIP
                  </span>
                  <p className="text-xs text-gray-400 mt-0.5">到期 {formatVipExpiry(customer)}</p>
                </div>
              ) : customer.vip_expired_ts ? (
                <div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-500">
                    <Crown className="w-3 h-3" />已过期
                  </span>
                  <p className="text-xs text-gray-400 mt-0.5">{formatVipExpiry(customer)}</p>
                </div>
              ) : (
                <span className="text-xs text-gray-400">普通</span>
              )}
            </td>
            <td className="px-4 py-3 text-center">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {customer.order_count || 0}
              </span>
            </td>
            <td className="px-4 py-3 text-right">
              <span className="font-medium text-green-600">
                ${parseFloat(customer.total_spent || 0).toFixed(2)}
              </span>
            </td>
            <td className="px-4 py-3 text-right">
              <span className={`font-medium ${parseFloat(customer.balance || 0) > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                ${parseFloat(customer.balance || 0).toFixed(2)}
              </span>
            </td>
            <td className="px-4 py-3 text-center">
              {customer.is_agent === 1 ? (
                <div>
                  <span className="text-xs font-medium text-purple-600">
                    {customer.agent_commission_rate}%
                  </span>
                  <p className="text-xs text-gray-400 mt-0.5">
                    累计 ${parseFloat(customer.agent_total_earnings || 0).toFixed(2)}
                  </p>
                </div>
              ) : (
                <span className="text-xs text-gray-300">—</span>
              )}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500">
              {customer.last_order_at
                ? new Date(customer.last_order_at).toLocaleDateString('zh-CN')
                : '暂无'}
            </td>
            <td className="px-4 py-3 text-center">
              <div className="flex items-center justify-center gap-1">
                <button onClick={() => onOpenDetail(customer)}
                  className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 rounded-lg hover:bg-blue-50">
                  详情
                </button>
                {isAdmin && (
                  <button onClick={() => onOpenVip(customer)}
                    className={`text-xs px-2 py-1 rounded-lg ${
                      isVipActive(customer)
                        ? 'text-yellow-600 hover:bg-yellow-50'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}>
                    {isVipActive(customer) ? '续费' : '开VIP'}
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    {pagination.pages > 1 && (
      <div className="px-4 py-3 border-t flex items-center justify-between">
        <p className="text-sm text-gray-500">
          第 {page} / {pagination.pages} 页，共 {pagination.total} 条
        </p>
        <div className="flex space-x-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">上一页</button>
          <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages}
            className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50">下一页</button>
        </div>
      </div>
    )}
  </div>
);

export default CustomerTable;